import { errorMessages as messages, successMessages as successMsgs } from './errorMessages';

// Re-export for convenience
export const errorMessages = messages;
export const successMessages = successMsgs;

/**
 * Internal function to translate error code string to Ukrainian
 */
function translateErrorCode(errorCode: string): string {
  if (!errorCode) {
    return errorMessages.default;
  }

  // 1. Check for exact match with error code
  if (errorMessages[errorCode]) {
    return errorMessages[errorCode];
  }

  // 2. Extract Firebase auth error code from message (e.g., "Firebase: auth/invalid-email")
  const firebaseAuthMatch = errorCode.match(/auth\/[\w-]+/);
  if (firebaseAuthMatch && errorMessages[firebaseAuthMatch[0]]) {
    return errorMessages[firebaseAuthMatch[0]];
  }

  // 3. Check for Firestore error codes in message
  const firestoreKeywords = ['permission-denied', 'not-found', 'already-exists', 'unauthenticated'];
  for (const keyword of firestoreKeywords) {
    if (errorCode.toLowerCase().includes(keyword)) {
      return errorMessages[keyword];
    }
  }

  // 4. Check for Storage error codes
  const storageMatch = errorCode.match(/storage\/[\w-]+/);
  if (storageMatch && errorMessages[storageMatch[0]]) {
    return errorMessages[storageMatch[0]];
  }

  // 5. Check for custom error codes
  const customMatch = errorCode.match(/custom\/[\w-]+/);
  if (customMatch && errorMessages[customMatch[0]]) {
    return errorMessages[customMatch[0]];
  }

  // 6. Check for partial matches (case-insensitive contains)
  const lowerErrorCode = errorCode.toLowerCase();
  for (const [key, message] of Object.entries(errorMessages)) {
    if (lowerErrorCode.includes(key.toLowerCase())) {
      return message;
    }
  }

  // 7. Check for common English error patterns and translate them
  const englishPatterns: Record<string, string> = {
    'password should be at least': errorMessages['auth/weak-password'],
    'password is too weak': errorMessages['auth/weak-password'],
    'invalid password': errorMessages['auth/wrong-password'],
    'wrong password': errorMessages['auth/wrong-password'],
    'invalid email': errorMessages['auth/invalid-email'],
    'email already in use': errorMessages['auth/email-already-in-use'],
    'user not found': errorMessages['auth/user-not-found'],
    'user disabled': errorMessages['auth/user-disabled'],
    'network error': errorMessages['auth/network-request-failed'],
    'network request failed': errorMessages['auth/network-request-failed'],
    'too many requests': errorMessages['auth/too-many-requests'],
    'popup closed': errorMessages['auth/popup-closed-by-user'],
    'popup blocked': errorMessages['auth/popup-blocked'],
    'permission denied': errorMessages['permission-denied'],
    'unauthenticated': errorMessages['unauthenticated'],
    'document not found': errorMessages['not-found'],
    'no such document': errorMessages['not-found'],
    // Ukrainian limit patterns from Cloud Functions
    'досягнуто ліміт': errorMessages['limit/projects'],
    'ліміт проектів': errorMessages['limit/projects'],
    'ліміт демо': errorMessages['limit/demo-projects'],
    'ліміт версій': errorMessages['limit/versions'],
    'ліміт екранів': errorMessages['limit/screens'],
    'ліміт подій': errorMessages['limit/events'],
    'ліміт учасників': errorMessages['limit/members'],
  };

  for (const [pattern, translation] of Object.entries(englishPatterns)) {
    if (lowerErrorCode.includes(pattern)) {
      return translation;
    }
  }

  // 8. Log untranslated error for debugging (only in development)
  if (import.meta.env.DEV) {
    console.warn('[translateError] No translation found for:', errorCode);
  }

  // Return default message if no translation found
  return errorMessages.default;
}

/**
 * Translates error codes/messages to Ukrainian
 * Supports Firebase errors, Firestore errors, Storage errors, and custom app errors
 */
export function translateError(error: Error | string | object | null | undefined): string {
  // DEBUG: Log error for INTERNAL errors
  if (error && typeof error === 'object') {
    const err = error as { code?: string };
    if (err.code === 'INTERNAL' || err.code === 'internal') {
      console.log('[DEBUG translateError] INTERNAL error received:', JSON.stringify(error, null, 2));
    }
  }
  
  if (!error) {
    return errorMessages.default;
  }

  // Handle object errors (Firebase error objects with code/message)
  if (typeof error === 'object' && !Array.isArray(error)) {
    const err = error as { code?: string; message?: string; name?: string };
    const errorCode = err.code || err.message || err.name || '';
    return translateErrorCode(errorCode);
  }

  // Handle Error instances
  if (error instanceof Error) {
    const errorCode = error.message || error.name || '';
    return translateErrorCode(errorCode);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return translateErrorCode(error);
  }

  return errorMessages.default;
}

/**
 * Gets a success message by key
 */
export function getSuccessMessage(key: string, defaultMessage?: string): string {
  return successMessages[key] || defaultMessage || successMessages['default/success'];
}

/**
 * Checks if an error is a specific Firebase auth error
 */
export function isAuthError(error: unknown, errorCode: string): boolean {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { code?: string; message?: string };
  const code = err.code || '';
  const message = err.message || '';
  
  return code === errorCode || 
         message.includes(errorCode) || 
         message.includes(`auth/${errorCode}`);
}

/**
 * Checks if error requires re-authentication
 */
export function requiresReauthentication(error: unknown): boolean {
  return isAuthError(error, 'auth/requires-recent-login');
}

/**
 * Checks if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  
  const err = error as { code?: string; message?: string };
  const code = err.code || '';
  const message = (err.message || '').toLowerCase();
  
  return code === 'auth/network-request-failed' ||
         code === 'unavailable' ||
         message.includes('network') ||
         message.includes('offline') ||
         message.includes('failed to fetch');
}

/**
 * Checks if error is a "not found" error
 */
export function isNotFoundError(error: unknown): boolean {
  return isAuthError(error, 'not-found') || 
         isAuthError(error, 'auth/user-not-found') ||
         isAuthError(error, 'storage/object-not-found');
}

/**
 * Checks if error is a permission denied error
 */
export function isPermissionDeniedError(error: unknown): boolean {
  return isAuthError(error, 'permission-denied') || 
         isAuthError(error, 'auth/unauthorized') ||
         isAuthError(error, 'storage/unauthorized');
}

/**
 * Checks if error is related to email verification
 */
export function isEmailVerificationError(error: unknown): boolean {
  return isAuthError(error, 'auth/email-not-verified');
}

/**
 * Formats error for display in UI
 * Removes technical details and returns user-friendly message
 */
export function formatErrorForDisplay(error: unknown): string {
  if (!error) {
    return errorMessages.default;
  }

  if (typeof error === 'string') {
    return translateError(error);
  }

  if (error instanceof Error) {
    return translateError(error);
  }

  // Handle Firebase error objects
  if (typeof error === 'object' && error !== null) {
    const err = error as { code?: string; message?: string };
    
    if (err.code) {
      return translateError(err.code);
    }
    
    if (err.message) {
      return translateError(err.message);
    }
  }

  return errorMessages.default;
}
